/**
 * moveSourceAdjacentToTarget moves an item from one part of the layout to the other, placing it
 * either to the left or right of the target based on hoverPosition. All other items shift to
 * accommodate the moved item.
 *
 * When hoverPosition is 'right' (the default), the source is inserted after the target. When
 * hoverPosition is 'left', the source is inserted before the target.
 *
 * Example (hoverPosition = 'right'):
 * Starting layout:    [Item 1][Item 2]
 *                     [Item 3][Item 4]
 * Move Item 4 → Item 1 (right):
 *                     [Item 1][Item 4]
 *                     [Item 2][Item 3]
 *
 * Example (hoverPosition = 'left'):
 * Starting layout:    [Item 1][Item 2]
 *                     [Item 3][Item 4]
 * Move Item 4 → Item 1 (left):
 *                     [Item 4][Item 1]
 *                     [Item 2][Item 3]
 *
 * @param oldLayout Starting layout
 * @param source The item to move
 * @param target The item to place the source adjacent to
 * @param hoverPosition Which side of the target to insert on ('left' or 'right')
 * @param comparator Optional equality check for items
 */
import _ from 'underscore';

import {HoverPosition} from '../hooks/useDnDCanvasDragState';

export default function moveSourceAdjacentToTarget<T>(
  oldLayout: Array<Array<T>>,
  source: T,
  target: T,
  hoverPosition: HoverPosition = 'right',
  comparator: (item1: T, item2: T) => boolean = (item1: T, item2: T) => item1 === item2
): {newLayout: Array<Array<T>>; layoutChanged: boolean} {
  if (comparator(source, target)) {
    return {
      layoutChanged: false,
      newLayout: oldLayout,
    };
  }

  const firstRowLength = oldLayout[0].length;
  const flatLayout = oldLayout.flat();

  const sourceIndex = flatLayout.findIndex((item) => comparator(source, item));
  if (sourceIndex < 0) {
    return {layoutChanged: false, newLayout: oldLayout};
  }
  flatLayout.splice(sourceIndex, 1);
  const targetIndex = flatLayout.findIndex((item) => comparator(target, item));
  if (targetIndex < 0) {
    return {layoutChanged: false, newLayout: oldLayout};
  }
  const newSourceIndex = hoverPosition === 'left' ? targetIndex : targetIndex + 1;

  flatLayout.splice(newSourceIndex, 0, source);

  return {
    layoutChanged: newSourceIndex !== sourceIndex,
    newLayout: _.chunk(flatLayout, firstRowLength) as Array<Array<T>>,
  };
}
